<template>
  <b-container class="installation_history_table" fluid>
    <b-alert
      show
      variant="warning"
      v-if="!installationHistoryData || !installationHistoryData.length"
    >This device has never been installed</b-alert>
    <b-table
      :fields="fields"
      :items="installationHistoryData"
      v-if="installationHistoryData && installationHistoryData.length"
    >
      <template v-slot:cell(install_time)="row">
        <template
          v-if="selectedEditRowIndex!=row.index"
        >{{row.item.install_time |formatDate(true) }}</template>
        <b-form-datepicker
          v-else
          v-model="row.item.install_time"
          :min="minInstallTime"
          :max="maxInstallTime"
          size="sm"
        ></b-form-datepicker>
      </template>

      <template v-slot:cell(uninstall_time)="row">
        <template v-if="!row.item.uninstall_time && selectedEditRowIndex!=row.index">N/A</template>

        <template v-else-if="!row.item.uninstall_time && selectedEditRowIndex==row.index">
          <b-button
            :id="'uninstall-device'"
            size="sm"
            variant="outline-danger"
            :disabled="!$base.isAllowed('uninstallDevice')"
            @click.prevent="uninstallDevice(row)"
          >Uninstall Device</b-button>
        </template>

        <template
          v-else-if="selectedEditRowIndex!=row.index"
        >{{row.item.uninstall_time | formatDate(true)}}</template>

        <b-form-datepicker
          v-else
          v-model="row.item.uninstall_time"
          :min="row.item.install_time"
          :max="maxUninstallTime"
          size="sm"
        ></b-form-datepicker>
      </template>

      <template v-slot:cell(circuit_name)="row">
        <template v-if="selectedEditRowIndex!=row.index">{{row.item.circuit_name}}</template>
        <b-form-select
          v-else
          v-model="selectedCircuitUuid"
          :options="circuitSelectOptions"
          @input="circuitSelectChange"
          size="sm"
        ></b-form-select>
      </template>

      <template v-slot:cell(pole_substation_number)="row">
        <template v-if="selectedEditRowIndex!=row.index">{{row.item.pole_substation_number}}</template>
        <b-form-select
          v-else
          v-model="selectedPoleUuid"
          :options="poleSelectOptions"
          @input="poleSelectChange"
          size="sm"
        ></b-form-select>
      </template>

      <template v-slot:cell(actions)="row">
        <b-button
          :id="'edit-installation-history-'+ row.index"
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          size="sm"
          variant="outline-primary"
          @click.prevent="editSelectedRow(row)"
        >
          <i class="fas fa-edit"></i>
        </b-button>
        <b-tooltip
          v-b-tooltip.hover.d50
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          :target="'edit-installation-history-'+ row.index"
          :container="'edit-installation-history-'+ row.index"
          :title="'Edit Installation Record'"
        />

        <b-button
          :id="'delete-installation-history-'+ row.index"
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          size="sm"
          variant="outline-primary"
          @click.prevent="deleteEditHistory(row)"
        >
          <i class="fas fa-trash"></i>
        </b-button>
        <b-tooltip
          v-b-tooltip.hover.d50
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          :target="'delete-installation-history-'+ row.index"
          :container="'delete-installation-history-'+ row.index"
          :title="'Delete Installation Record'"
        />

        <b-button
          :id="'insert-above-installation-history-'+ row.index"
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive && row.item.uninstall_time"
          size="sm"
          variant="outline-primary"
          @click.prevent="insertInstallationHistory(row, 'above')"
        >
          <i class="fas fa-angle-double-up"></i>
        </b-button>
        <b-tooltip
          v-b-tooltip.hover.d50
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          :target="'insert-above-installation-history-'+ row.index"
          :container="'insert-above-installation-history-'+ row.index"
          :title="'Insert Duplicate Installation Record Above This Installation'"
        />

        <b-button
          :id="'insert-below-installation-history-'+ row.index"
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          size="sm"
          variant="outline-primary"
          @click.prevent="insertInstallationHistory(row, 'below')"
        >
          <i class="fas fa-angle-double-down"></i>
        </b-button>
        <b-tooltip
          v-b-tooltip.hover.d50
          v-if="$base.isAllowed('editInstallationHistory') && editModeActive"
          :target="'insert-below-installation-history-'+ row.index"
          :container="'insert-below-installation-history-'+ row.index"
          :title="'Insert Duplicate Installation Record Below This Installation'"
        />

        <b-button
          :id="'cancel-installation-history-'+ row.index"
          v-if="$base.isAllowed('editInstallationHistory') && selectedEditRowIndex==row.index"
          size="sm"
          variant="outline-danger"
          @click.prevent="cancelEditHistory()"
        >Cancel</b-button>

        <span :id="'save-installation-history-wrapper-'+ row.index">
          <b-button
            :id="'save-installation-history-'+ row.index"
            v-if="$base.isAllowed('editInstallationHistory') && selectedEditRowIndex==row.index"
            size="sm"
            variant="outline-success"
            :disabled="!selectedPoleUuid"
            @click.prevent="saveHistory()"
          >Save</b-button>
        </span>
        <b-tooltip
          :target="'save-installation-history-wrapper-'+ row.index"
          v-if="!selectedPoleUuid"
        >You must select a Pole to save</b-tooltip>
      </template>
    </b-table>
  </b-container>
</template>
<script>
import {circuitService} from "@/_services";
import { mapState, mapActions } from "vuex";
import { cloneDeep } from "lodash";
export default {
  name: "InstallationHistoryTab",
  data() {
    return {
      selectedEditRowIndex: -1,
      selectedCircuitUuid: null,
      selectedPoleUuid: null,
      selectedEditRowCleanCopy: null,
      fields: [
        {
          key: "install_time",
          label: "INSTALL DATE"
        },
        {
          key: "uninstall_time",
          label: "UNINSTALL DATE"
        },
        {
          key: "circuit_name",
          label: "CIRCUIT NAME"
        },
        {
          key: "pole_substation_number",
          label: "Substation Number"
        },
        {
          key: "actions",
          label: "ACTIONS"
        }
      ],
      circuits: []
    };
  },
  computed: {
    ...mapState("device", {
      installationHistory: state => state.installation_history
    }),
    ...mapState("layout", {
      hideInactiveCircuits: state => state.hideInactiveCircuits
    }),
    circuitSelectOptions: function() {
      return this.circuits.map(function(item) {
        return { value: item.uuid, text: item.name };
      });
    },
    poleSelectOptions: function() {
      // Get the select list, generated from the 'SelectedCircuit.poles' data
      return this.circuits
        .find(circuit => {
          return circuit.uuid === this.selectedCircuitUuid;
        })
        .poles.map(item => {
          return { value: item.uuid, text: item.pole_substation_number };
        });
    },
    installationHistoryData: function() {
      return this.installationHistory
        ? this.installationHistory.install_history
        : [];
    },
    editModeActive: function() {
      return this.selectedEditRowIndex == -1;
    },
    minInstallTime: function() {
      return this.selectedEditRowIndex !=
        this.installationHistoryData.length - 1
        ? this.installationHistoryData[this.selectedEditRowIndex + 1]
            .uninstall_time
        : null;
    },
    maxInstallTime: function() {
      return this.installationHistoryData[this.selectedEditRowIndex]
        .uninstall_time
        ? this.installationHistoryData[this.selectedEditRowIndex].uninstall_time
        : new Date();
    },
    maxUninstallTime: function() {
      return this.selectedEditRowIndex === 0 ? new Date() : this.installationHistoryData[this.selectedEditRowIndex - 1].install_time;
    },
  },
  methods: {
    ...mapActions("device", ["getDeviceInstallationHistory", "saveDeviceInstallationHistory"]),
    editSelectedRow: function(row) {
      // Set 'selected' varaibles,
      this.selectedCircuitUuid = row.item.circuit_uuid;
      this.selectedPoleUuid = row.item.pole_uuid;
      this.selectedEditRowIndex = row.index;
      this.selectedEditRowCleanCopy = cloneDeep(row.item);
    },
    async findAllCircuits() {
      this.circuits = (await circuitService.getAllCircuits())
    },
    exitEditMode: function() {
      // Reset some variables
      this.selectedEditRowIndex = -1;
      this.selectedEditRowCleanCopy = null;
    },
    saveHistory: function() {
      this.saveDeviceInstallationHistory({
        serial: this.$route.params.serial,
        installationHistory: this.installationHistory
      })
      .then(() => {
        this.exitEditMode();
      })
      .catch(() => {
        // If saving fails, reset the data to the previous copy of the row
        this.installationHistoryData.splice(
          this.selectedEditRowIndex,
          1,
          this.selectedEditRowCleanCopy
        );
        this.exitEditMode();
      });
    },
    cancelEditHistory: function() {
      // When cancelling, in the history data, input the clean copy and delete the existing record at this row (replace with clean copy)
      this.installationHistoryData.splice(
        this.selectedEditRowIndex,
        1,
        this.selectedEditRowCleanCopy
      );
      this.exitEditMode();
    },
    deleteEditHistory: function(row) {
      let confirmText =
        "Are you sure you want to delete this record of installation?";
      // If the there is no install time, then the device will be uninstalled from the pole - add additional confirmation text
      if (!row.item.uninstall_time) {
        confirmText += " This will uninstall this device from the pole";
      }
      this.$bvModal
        .msgBoxConfirm(confirmText, {
          title: "Delete Installation History Record",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes",
          cancelTitle: "No",
          footerClass: "p-2",
          hideHeaderClose: false
        })
        .then(deleteConfirmed => {
          // If the user wants to proceed, remove the item from the array and save
          if (deleteConfirmed) {
            this.installationHistoryData.splice(row.index, 1);
            this.saveHistory();
          }
        });
    },
    uninstallDevice: function(row) {
      // This is to uninstall the device from the currently installed pole
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to uninstall this device from it's current location?",
          {
            title: "Uninstall Device",
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: "Yes",
            cancelTitle: "No",
            footerClass: "p-2",
            hideHeaderClose: false
          }
        )
        .then(uninstallConfirmed => {
          if (uninstallConfirmed) {
            this.installationHistoryData[row.index].uninstall_time = new Date();
            this.saveHistory();
          }
        });
    },
    insertInstallationHistory: function(row, insertPosition) {
      let duplicatedRow = cloneDeep(row.item);
      if (insertPosition === "above") {
        // If positioning above, copy the row, then set the install time to the uninstall time and add to the correct position
        duplicatedRow.install_time = duplicatedRow.uninstall_time;
        this.installationHistoryData.splice(row.index, 0, duplicatedRow);
      } else {
        // If positioning below, copy the row, then set the uninstall time to the install time and add to the correct position
        duplicatedRow.uninstall_time = duplicatedRow.install_time;
        this.installationHistoryData.splice(row.index + 1, 0, duplicatedRow);
      }
      this.saveHistory();
    },
    circuitSelectChange: function(circuitUuid) {
      if (
        this.installationHistoryData[this.selectedEditRowIndex].circuit_uuid !== circuitUuid
      ) {
        this.installationHistoryData[
          this.selectedEditRowIndex
        ].circuit_uuid = circuitUuid;
        this.selectedPoleUuid = null;
      }
    },
    poleSelectChange: function(poleUuid) {
      this.installationHistoryData[
        this.selectedEditRowIndex
      ].pole_uuid = poleUuid;
    },
    // This method removes the actions column if the user doesn't have permission to modify so that screen space isn't wasted
    filterActionsColumn: function() {
      if (!this.$base.isAllowed("editInstallationHistory")) {
        this.fields = this.fields.filter(field => field.key !== "actions");
      }
    }
  },
  async mounted() {
    this.filterActionsColumn();
    await this.getDeviceInstallationHistory(this.$route.params.serial);
    await this.findAllCircuits(this.hideInactiveCircuits);
  }
};
</script>
<style src="./InstallationHistoryTab.scss" lang="scss" scoped/>
